import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface WhatsappConfig {
  id: string;
  provider: string;
  phoneNumberId: string;
  accessToken: string;
  businessAccountId?: string;
  isActive: boolean;
}

const WhatsappConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<WhatsappConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    provider: 'META_CLOUD',
    phoneNumberId: '',
    accessToken: '',
    businessAccountId: '',
    isActive: true,
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Test Dialog State
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testTarget, setTestTarget] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/system-settings/communication/whatsapp-config');
      if (data && data.length > 0) {
        const active = data.find((c: WhatsappConfig) => c.isActive) || data[0];
        setConfig(active);
        setFormData({
          provider: active.provider,
          phoneNumberId: active.phoneNumberId,
          accessToken: active.accessToken, // Backend might mask
          businessAccountId: active.businessAccountId || '',
          isActive: active.isActive,
        });
      }
    } catch (error) {
      console.error('Failed to fetch WhatsApp config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleTestOpen = () => {
    setTestTarget('');
    setTestDialogOpen(true);
  };

  const handleTestClose = () => {
    setTestDialogOpen(false);
  };

  const handleTestSubmit = async () => {
    if (!config || !testTarget) return;
    
    setTesting(true);
    try {
      const { data } = await axios.post(`/system-settings/communication/whatsapp-config/${config.id}/test`, {
        target: testTarget
      });
      
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        handleTestClose();
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to send test message' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      if (config) {
        await axios.patch(`/system-settings/communication/whatsapp-config/${config.id}`, formData);
        setMessage({ type: 'success', text: 'WhatsApp Configuration updated successfully' });
      } else {
        await axios.post('/system-settings/communication/whatsapp-config', formData);
        setMessage({ type: 'success', text: 'WhatsApp Configuration created successfully' });
        fetchConfig();
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to save configuration' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <>
      <div className="page-header">
        <h1>WhatsApp Configuration</h1>
        <p>Configure WhatsApp Business API provider and credentials</p>
      </div>

      <Paper sx={{ p: 3 }}>
        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Provider</InputLabel>
              <Select
                name="provider"
                value={formData.provider}
                label="Provider"
                onChange={handleChange as any}
              >
                <MenuItem value="META_CLOUD">Meta Cloud API (Official)</MenuItem>
                <MenuItem value="TWILIO">Twilio</MenuItem>
                <MenuItem value="WATI">Wati</MenuItem>
                <MenuItem value="INTERAKT">Interakt</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number ID"
              name="phoneNumberId"
              value={formData.phoneNumberId}
              onChange={handleChange}
              required
              helperText="From Meta App Dashboard"
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              label="Access Token / API Key"
              name="accessToken"
              value={formData.accessToken}
              onChange={handleChange}
              required
              type="password"
              helperText="Permanent Access Token recommended"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Business Account ID"
              name="businessAccountId"
              value={formData.businessAccountId}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label={formData.isActive ? "Active" : "Inactive"}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            
            {config && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SendIcon />}
                onClick={handleTestOpen}
                disabled={!config.isActive}
              >
                Test Configuration
              </Button>
            )}
          </Grid>
        </Grid>
      </form>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onClose={handleTestClose}>
        <DialogTitle>Send Test WhatsApp Message</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a phone number to send a test WhatsApp message using the current configuration.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={testTarget}
            onChange={(e) => setTestTarget(e.target.value)}
            placeholder="+919876543210"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestClose}>Cancel</Button>
          <Button 
            onClick={handleTestSubmit} 
            variant="contained" 
            disabled={!testTarget || testing}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
    </>
  );
};

export default WhatsappConfigPage;
